import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { PageTitle } from '../../../_metronic/layout/core'
import { getErrorCode } from '../../common/api/errors'
import { getMatchingDecision } from './core/api'
import { holdLabel } from './core/dispute'
import { depositStatusLabel } from '../matching/routes/DepositPayoutTimeline/core/consts'
import { DISPUTE_STATUS_BADGE, DISPUTE_STATUS_LABEL } from '../matching/routes/Disputes/core/consts'
import {
  BODY,
  BORDER,
  FACT_GRID,
  INK,
  MUTED,
  NO_BREADCRUMBS,
  RAW_BLOCK,
  RULE_BAR_COLOUR,
} from './core/consts'
import { MatchingDecision } from './core/response.types'
import { formatMskDateTime } from '../matching/routes/Dashboard/core/mskTime'
import { MatchingTicketLink } from '../../common/MatchingTicketLink'
import TicketActionsBar from './components/TicketActionsBar'
import { Fact } from './components/Fact'

/**
 * Lives in the matching direction: matching is moving into its own service and will take this
 * screen with it, so the ticket card knows only a link to it.
 */
const MatchingTicketCardPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const [decision, setDecision] = useState<MatchingDecision | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [rawOpen, setRawOpen] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!token) return

    let cancelled = false
    setDecision(null)
    setError(null)

    const load = async () => {
      try {
        const { data } = await getMatchingDecision(token)

        if (cancelled) return

        if (!data.success) {
          setError(data.errors.general_error ?? 'Не удалось загрузить разбор')

          return
        }

        setDecision(data.data)
      } catch (e) {
        if (cancelled) return

        setError(
          getErrorCode(e) === 'TICKET_NOT_FOUND'
            ? 'Решения мэтчинга по этому тикету нет'
            : 'Не удалось загрузить разбор',
        )
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [token, reloadKey])

  return (
    <>
      <PageTitle breadcrumbs={NO_BREADCRUMBS}>Матчинг-тикет</PageTitle>

      <div className='d-flex align-items-center' style={{ gap: 12, marginBottom: 18 }}>
        <span className='badge badge-light-info'>Сервис мэтчинга</span>
        <div style={{ flexGrow: 1 }} />
        {decision && (
          <TicketActionsBar header={decision.header} onChanged={() => setReloadKey((n) => n + 1)} />
        )}
        <Link to={`/payment-tickets/card/${token}`} className='btn btn-sm btn-light-primary'>
          Полная карточка тикета
        </Link>
      </div>

      {error && (
        <div className='card' style={{ padding: '24px 28px' }}>
          <div style={{ fontSize: 14, color: MUTED }}>{error}</div>
        </div>
      )}

      {!error && !decision && (
        <div className='d-flex justify-content-center' style={{ padding: 48 }}>
          <Spinner animation='border' variant='primary' />
        </div>
      )}

      {!error && decision && (
        <div className='card' style={{ marginBottom: 18 }}>
          <div
            className='d-flex align-items-center'
            style={{ minHeight: 56, padding: '0 28px', borderBottom: `1px solid ${BORDER}` }}
          >
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: INK }}>Тикет</h2>
          </div>
          <div style={FACT_GRID}>
            <Fact label='Статус' value={depositStatusLabel(decision.header.status)} />
            <Fact
              label='Сумма'
              value={
                decision.header.amount === null
                  ? '—'
                  : `${decision.header.amount.toLocaleString('ru-RU')} ${decision.header.currency}`
              }
            />
            <Fact label='Мерчант' value={decision.header.merchant ?? '—'} />
            <Fact label='Метод' value={decision.header.paymentMethod ?? '—'} />
            <Fact label='Создан' value={formatMskDateTime(decision.header.createdAt)} />
            <Fact
              label='Закрыт'
              value={
                decision.header.completionDate
                  ? formatMskDateTime(decision.header.completionDate)
                  : '—'
              }
            />
            {decision.header.closeReasonTitle && (
              <Fact label='Причина закрытия' value={decision.header.closeReasonTitle} />
            )}
            {/* Спор — факт тикета, а не пары: депозит, которому мэтчинг отказал, а реквизит выдал
                другой адаптер, спорится без матча, и в блоке пары этот факт был бы не виден. */}
            {decision.header.disputeStatus !== null && (
              <div className='d-flex flex-column' style={{ gap: 3 }}>
                <div style={{ fontSize: 11.5, color: MUTED, textTransform: 'uppercase' }}>Спор</div>
                <div>
                  <span
                    className={`badge ${
                      DISPUTE_STATUS_BADGE[decision.header.disputeStatus] ?? 'badge-light'
                    }`}
                  >
                    {DISPUTE_STATUS_LABEL[decision.header.disputeStatus] ??
                      decision.header.disputeStatus}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!error && decision?.match && (
        <div className='card' style={{ marginBottom: 18 }}>
          <div
            className='d-flex align-items-center'
            style={{ minHeight: 56, padding: '0 28px', gap: 10, borderBottom: `1px solid ${BORDER}` }}
          >
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: INK }}>Пара найдена</h2>
            <span style={{ fontSize: 12.5, color: MUTED }}>
              отказ и матч не исключают друг друга — депозит мог сначала получить отказ
            </span>
          </div>
          <div style={FACT_GRID}>
            <Fact label='Сумма матча' value={`${decision.match.amount.toLocaleString('ru-RU')} ₽`} />
            <Fact label='Сматчен' value={formatMskDateTime(decision.match.matchedAt)} />
            <Fact label='Статус мэтчинга' value={decision.match.matchingStatus ?? '—'} />
            <Fact
              label='Бронь выплаты по спору'
              value={holdLabel(decision.header.disputeStatus, decision.match.heldUntil)}
            />
            <div className='d-flex flex-column' style={{ gap: 3 }}>
              <div style={{ fontSize: 11.5, color: MUTED, textTransform: 'uppercase' }}>Выплата</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: INK }}>
                <MatchingTicketLink token={decision.match.payoutToken} type='payout' empty='—' />
              </div>
            </div>
            <Fact label='Статус выплаты' value={decision.match.payoutStatus ?? '—'} />
          </div>
        </div>
      )}

      {/* Разбирать нечего: журнал решений пишется только когда реквизит на месте не выдан, поэтому
          у сматченного сразу депозита строки нет вовсе. Четыре нуля и прочерк на её месте читались
          как «данные не доехали». */}
      {!error && decision && decision.decidedAt === null && (
        <div className='card' style={{ padding: '20px 28px' }}>
          <div style={{ fontSize: 13.5, color: BODY }}>
            Выплата подобрана сразу — отказа не было. Мэтчинг записывает разбор решения только
            тогда, когда реквизит на месте не выдан.
          </div>
        </div>
      )}

      {!error && decision && decision.decidedAt !== null && (
        <div className='d-flex' style={{ gap: 18, alignItems: 'flex-start' }}>
          <div className='card' style={{ width: 396, flexShrink: 0 }}>
            <div
              className='d-flex align-items-center'
              style={{ minHeight: 56, padding: '0 24px', borderBottom: `1px solid ${BORDER}` }}
            >
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: INK }}>Данные решения</h2>
            </div>
            <div className='d-flex flex-column' style={{ padding: '18px 24px', gap: 13 }}>
              {[
                ['Рассмотрено выплат', String(decision.candidates)],
                ['Было открыто выплат', String(decision.availablePayouts)],
                ['Подошло', String(decision.fit)],
                // Отказа могло не быть — у сматченного сразу депозита момента решения нет, и
                // форматирование пустого значения давало «Invalid date».
                ['Время решения', decision.decidedAt ? formatMskDateTime(decision.decidedAt) : '—'],
              ].map(([label, value]) => (
                <div key={label} className='d-flex align-items-baseline' style={{ gap: 12 }}>
                  <div style={{ width: 160, flexShrink: 0, fontSize: 12.5, color: MUTED }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: INK }}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className='card' style={{ flexGrow: 1, minWidth: 0 }}>
            <div
              className='d-flex align-items-center'
              style={{ minHeight: 56, padding: '0 28px', gap: 10, borderBottom: `1px solid ${BORDER}` }}
            >
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: INK }}>
                Что дисквалифицировало кандидатов
              </h2>
              <span style={{ fontSize: 12.5, color: MUTED }}>
                одна выплата может не пройти по нескольким правилам
              </span>
            </div>

            <div className='d-flex flex-column' style={{ padding: '20px 28px', gap: 15 }}>
              {decision.nearestMissSummary && (
                <div style={{ fontSize: 14, color: BODY }}>
                  Ближайшая выплата: {decision.nearestMissSummary}
                </div>
              )}

              {decision.breakdown.map((rule) => (
                <div key={rule.code} className='d-flex flex-column' style={{ gap: 6 }}>
                  <div className='d-flex align-items-baseline' style={{ gap: 10 }}>
                    <div style={{ flexGrow: 1, fontSize: 13, fontWeight: 500, color: BODY }}>
                      {rule.label}
                    </div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: INK }}>{rule.candidates}</div>
                    <div style={{ width: 44, textAlign: 'right', fontSize: 12, color: MUTED }}>
                      {rule.share} %
                    </div>
                  </div>
                  <div style={{ height: 7, borderRadius: 4, background: BORDER, overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${Math.min(rule.share, 100)}%`,
                        height: 7,
                        background: RULE_BAR_COLOUR(rule.code),
                      }}
                    />
                  </div>
                </div>
              ))}

              {decision.raw && (
                <div className='d-flex flex-column' style={{ gap: 8 }}>
                  <button
                    type='button'
                    className='btn btn-sm btn-light align-self-start'
                    style={{ fontSize: 12.5 }}
                    onClick={() => setRawOpen((open) => !open)}
                  >
                    {rawOpen ? 'Скрыть сырые данные' : 'Показать сырые данные'}
                  </button>
                  {rawOpen && (
                    <pre style={{ ...RAW_BLOCK, margin: 0, maxHeight: 420 }}>
                      {JSON.stringify(decision.raw, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MatchingTicketCardPage
